import React from 'react'
import {
  i18nTranslate,
  getImageFromProperty,
  currencyFormat,
  getMonthStringList,
} from 'src/utils'
import { ACCOUNT_TYPE_BRAND_AFFILIATE } from 'src/utils/constant'
import { customerContainer } from 'src/models'

const SubscriptionCycleIcon = () => (
  <svg
    className="subscription-cycle-icon"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true">
    <circle cx="12" cy="12" r="12" fill="#C9A26B" />
    <path
      d="M7.2 11.9a4.8 4.8 0 0 1 7.8-3.7"
      stroke="#FFFFFF"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M15.2 5.8v2.8h-2.9"
      stroke="#FFFFFF"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16.8 12.2a4.8 4.8 0 0 1-7.8 3.7"
      stroke="#FFFFFF"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M8.8 18.2v-2.8h2.9"
      stroke="#FFFFFF"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

/**
 * Returns projected next processing date label for a given frequency in months.
 *
 * @param {number} frequencyPeriod - Number of months until next processing
 * @returns {string} Formatted processing date label
 */
function getNextOrderDateLabel(frequencyPeriod = 1) {
  const nextDate = new Date()
  nextDate.setMonth(nextDate.getMonth() + Number(frequencyPeriod))
  const month = getMonthStringList()[nextDate.getMonth()]
  const day = nextDate.getDate()
  const year = nextDate.getFullYear()
  const processingLabel = i18nTranslate(
    'subscriptionAccordion.processingLabel',
    'Processing'
  )
  return `${processingLabel} ${month} ${day}, ${year}`
}

/**
 * Returns frequency text label for subscription items.
 *
 * @param {number} frequencyPeriod - Number of months between subscription orders
 * @returns {string} Human-readable frequency text
 */
function getFrequencyText(frequencyPeriod) {
  const freq = Number(frequencyPeriod) || 1
  if (freq === 1) {
    return i18nTranslate(
      'subscriptionAccordion.monthlySubscription',
      'Monthly subscription'
    )
  }
  return i18nTranslate(
    'subscriptionAccordion.frequencyEveryMonths',
    'Every {freq} months subscription'
  ).replace('{freq}', freq)
}

function getNormalSubscriptionItemsPrice(item) {
  const userRole = customerContainer.profileResponse?.accounts?.type || ''
  const priceAfterDiscount = item?.totalValue?.priceAfterDiscount
  const isPriceFreePromotion =
    userRole?.includes(ACCOUNT_TYPE_BRAND_AFFILIATE) &&
    (priceAfterDiscount === 0 || priceAfterDiscount === '0')
  const rawPrice = isPriceFreePromotion
    ? '0'
    : priceAfterDiscount || item?.totalValue?.originalPrice || '0'
  return rawPrice ? currencyFormat({ price: rawPrice }) : ''
}

/**
 * Returns combined normal + consumable next subscription list without grouping.
 *
 * @param {Array} normalSubscriptionItems - Normal subscription items
 * @param {Array} consumableDetailsList - Consumable subscription details
 * @returns {Array} Combined subscription items sorted by frequency period
 */
function getNextSubscriptionItems(
  normalSubscriptionItems = [],
  consumableDetailsList = []
) {
  const normalItems = normalSubscriptionItems.map(item => {
    const freq = item?.subscription?.frequencyPeriod || 1
    const sku = item?.skus?.[0] || {}
    return {
      name: sku?.skuProperties?.name || '',
      quantity: item?.quantity || 1,
      imageURL: getImageFromProperty(sku?.skuProperties),
      price: getNormalSubscriptionItemsPrice(item),
      frequencyPeriod: Number(freq) || 1,
      processingDateLabel: getNextOrderDateLabel(freq),
    }
  })

  const consumableItems = consumableDetailsList.map(detail => {
    const freq = detail?.frequencyPeriod || 1
    return {
      name: detail?.name || detail?.skuId || '',
      quantity: detail?.quantity || 1,
      imageURL: detail?.imageURL || '',
      price: detail?.price || '0',
      frequencyPeriod: Number(freq) || 1,
      processingDateLabel: getNextOrderDateLabel(freq),
    }
  })

  return [...normalItems, ...consumableItems].sort(
    (a, b) => a.frequencyPeriod - b.frequencyPeriod
  )
}

/**
 * Subscription order tile displaying today's subscription items (initial order).
 * Shows today's date, items from normalSubscriptionItems array.
 * Pure functional component - no state or lifecycle.
 */
const SubscriptionTodayOrderTile = ({ normalSubscriptionItems }) => {
  return (
    <div className="subscription-order-tile my-3">
      <div className="subscription-tile-header d-flex justify-content-between align-items-center mb-2">
        <span className="font-weight-bold d-flex align-items-center">
          <SubscriptionCycleIcon />
          {i18nTranslate(
            'subscriptionAccordion.todayOrderTitle',
            "Today's Subscription Order"
          )}
        </span>
      </div>
      <div className="subscription-tile-block">
        <div className="subscription-tile-date text-capitalize">
          {getNextOrderDateLabel(0)}
        </div>
        {normalSubscriptionItems.map((item, idx) => {
          const sku = item?.skus?.[0] || {}
          const imgURL = getImageFromProperty(sku?.skuProperties)
          const price = getNormalSubscriptionItemsPrice(item)
          return (
            <div
              key={idx}
              className="subscription-tile-item d-flex align-items-center mb-2">
              {imgURL && (
                <img
                  src={imgURL}
                  alt={sku?.skuProperties?.name || ''}
                  className="subscription-tile-img mr-2"
                />
              )}
              <div className="subscription-tile-details flex-grow-1">
                <div className="d-flex justify-content-between">
                  <span className="subscription-tile-name">
                    {sku?.skuProperties?.name || ''}
                  </span>
                  {price && (
                    <span className="subscription-tile-price">{price}</span>
                  )}
                </div>
                <div>
                  <span className="d-block">
                    {i18nTranslate('subscriptionAccordion.qty', 'Quantity')}
                    {': '}
                    {item?.quantity || 1}
                  </span>
                  <span className="subscription-tile-freq">
                    {getFrequencyText(item?.subscription?.frequencyPeriod || 1)}
                  </span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Subscription order tile displaying next subscription items (future orders).
 * Combines normalSubscriptionItems and consumableDetailsList, sorted by frequency period.
 * Shows processing date labels and disclaimer. Pure functional component - no state or lifecycle.
 */
const SubscriptionNextOrderTile = ({
  normalSubscriptionItems = [],
  consumableDetailsList = [],
}) => {
  const nextSubscriptionItems = getNextSubscriptionItems(
    normalSubscriptionItems,
    consumableDetailsList
  )

  return (
    <div className="subscription-order-tile my-3">
      <div className="subscription-tile-header d-flex align-items-center mb-2">
        <span className="d-flex align-items-center">
          <SubscriptionCycleIcon />
          {i18nTranslate(
            'subscriptionAccordion.nextOrderTitle',
            'Next Subscription Order'
          )}
        </span>
      </div>
      {nextSubscriptionItems.map((item, itemIdx) => (
        <div key={itemIdx} className="subscription-next-item-block">
          <div className="subscription-item-processing-date">
            {item.processingDateLabel}
          </div>
          <div className="subscription-tile-item d-flex align-items-center mb-1">
            {item.imageURL && (
              <img
                src={item.imageURL}
                alt={item.name}
                className="subscription-tile-img mr-2"
              />
            )}
            <div className="subscription-tile-details flex-grow-1">
              <div className="d-flex justify-content-between">
                <span className="subscription-tile-name">{item.name}</span>
                {item.price && (
                  <span className="subscription-tile-price">{item.price}</span>
                )}
              </div>
              <div>
                <span className="d-block">
                  {i18nTranslate('subscriptionAccordion.qty', 'Quantity')}
                  {': '}
                  {item.quantity}
                </span>
                <span className="subscription-tile-freq">
                  {getFrequencyText(item.frequencyPeriod)}
                </span>
              </div>
            </div>
          </div>
          {itemIdx !== nextSubscriptionItems.length - 1 && (
            <div className="subscription-item-divider" />
          )}
        </div>
      ))}
      <div className="subscription-next-order-disclaimer">
        {i18nTranslate(
          'subscriptionAccordion.nextOrderDisclaimer',
          `Subtotals do not include shipping, taxes or items already on subscription (see Subscription Dashboard for monthly totals).`
        )}
      </div>
    </div>
  )
}

export {
  SubscriptionTodayOrderTile,
  SubscriptionNextOrderTile,
  getNextOrderDateLabel,
  getFrequencyText,
  getNextSubscriptionItems,
}
